import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import {
  hasValidGoogleMapsApiKey,
  resolveGoogleMapsApiKey,
} from '../config/resolveGoogleMapsApiKey';

const GOOGLE_MAPS_API_KEY = resolveGoogleMapsApiKey();
const LIBRARIES: 'places'[] = ['places'];
const MISSING_API_KEY_ERROR = new Error('REACT_APP_GOOGLE_MAPS_API_KEY n’est pas configurée');
const GOOGLE_MAPS_INVARIANT_ERROR = new Error('Google Maps est indisponible pour le moment');

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // resolveGoogleMapsApiKey always returns a usable browser key (env or fallback).
  // Guard kept for empty override / future strict mode.
  if (!hasValidGoogleMapsApiKey(GOOGLE_MAPS_API_KEY)) {
    return (
      <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: MISSING_API_KEY_ERROR }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  return (
    <GoogleMapsErrorBoundary>
      <GoogleMapsScriptProvider>{children}</GoogleMapsScriptProvider>
    </GoogleMapsErrorBoundary>
  );
};

class GoogleMapsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | undefined }
> {
  state: { error: Error | undefined } = { error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('[GoogleMaps] loader indisponible:', error);
  }

  render() {
    if (this.state.error) {
      return (
        <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: GOOGLE_MAPS_INVARIANT_ERROR }}>
          {this.props.children}
        </GoogleMapsContext.Provider>
      );
    }

    return this.props.children;
  }
}

const GoogleMapsScriptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authError, setAuthError] = useState<Error | undefined>();

  useEffect(() => {
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthError(new Error('La clé Google Maps a été refusée par Google'));
      previousAuthFailure?.();
    };

    return () => {
      window.gm_authFailure = previousAuthFailure;
    };
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });
  const effectiveLoadError = loadError || authError;
  // When useLoadScript reports loaded with libraries:['places'], Places is available.
  // Do not AND with a sync hasPlacesApi check (can race and leave AddressAutocomplete dead
  // without loadError — silent failure). Auth errors still gate isLoaded.
  const mapsReady = Boolean(isLoaded && !authError && window.google?.maps?.places);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded: mapsReady, loadError: effectiveLoadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};
